import React, { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { generateMergePreview } from '../../utils/customerMergeUtils';

/**
 * Dialog for matching a customer to an existing customer for merge
 */
const CustomerMatchDialog = ({ 
  sourceCustomer, 
  allCustomers, // Still accept as prop for backward compatibility
  onClose, 
  onConfirmMerge 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customers, setCustomers] = useState(allCustomers || []);
  
  // Fetch ALL customers on mount for comprehensive matching
  useEffect(() => {
    const fetchAllCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const q = query(
          collection(db, collectionPath('customers')),
          orderBy('customerName', 'asc')
        );
        const snapshot = await getDocs(q);
        const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCustomers(allDocs);
        console.log(`✅ Loaded ${allDocs.length} customers for matching`);
      } catch (error) {
        console.error('Error loading customers:', error);
        // Fall back to prop customers if fetch fails
        setCustomers(allCustomers || []);
      } finally {
        setLoadingCustomers(false);
      }
    };
    
    fetchAllCustomers();
  }, []); // Only run once on mount
  
  // Filter out the source customer from available targets
  const availableTargets = useMemo(() => {
    return customers.filter(c => c.id !== sourceCustomer.id);
  }, [customers, sourceCustomer.id]);
  
  // Generate fuzzy match suggestions - PRIORITIZE CUSTOMER NAME MATCHING
  const suggestedMatches = useMemo(() => {
    if (!sourceCustomer.customerName) return [];
    
    const sourceName = sourceCustomer.customerName.trim();
    const results = [];
    const seenIds = new Set();
    
    // Strategy 1: Extract key words from source name and find customers containing those words
    // This handles "Keystone Golf" finding "Vail Resorts - Keystone Resort Golf"
    const sourceWords = sourceName.toLowerCase().split(/\s+/).filter(w => w.length >= 3);
    
    availableTargets.forEach(customer => {
      if (!customer.customerName || seenIds.has(customer.id)) return;
      
      const targetName = customer.customerName.toLowerCase();
      
      // Count how many source words appear in target name
      let matchedWords = 0;
      let totalSourceWords = sourceWords.length;
      
      sourceWords.forEach(word => {
        // Skip common words
        if (['the', 'and', 'inc', 'llc', 'dba', 'golf', 'club', 'country', 'tennis'].includes(word)) {
          totalSourceWords--; // Don't count common words
          return;
        }
        if (targetName.includes(word)) {
          matchedWords++;
        }
      });
      
      // Calculate word match score (how many unique words match)
      // Cap at 95% since 100% would have auto-matched during import
      if (matchedWords > 0 && totalSourceWords > 0) {
        const wordMatchScore = (matchedWords / totalSourceWords) * 100;
        if (wordMatchScore >= 50) { // At least 50% of significant words match
          results.push({
            customer,
            similarity: Math.min(95, Math.round(wordMatchScore)), // Cap at 95%
            matchReason: 'Customer Name'
          });
          seenIds.add(customer.id);
        }
      }
    });
    
    // Strategy 2: Standard fuzzy matching with Fuse.js for partial string matches
    const nameFuse = new Fuse(availableTargets, {
      keys: [
        { name: 'customerName', weight: 1.0 }
      ],
      threshold: 0.5, // More lenient - allow 50%+ similarity
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 3
    });
    
    const fuseResults = nameFuse.search(sourceName);
    fuseResults.slice(0, 10).forEach(result => {
      if (!seenIds.has(result.item.id)) {
        results.push({
          customer: result.item,
          similarity: Math.round((1 - result.score) * 100),
          matchReason: 'Customer Name'
        });
        seenIds.add(result.item.id);
      }
    });
    
    const nameSuggestions = results.sort((a, b) => b.similarity - a.similarity).slice(0, 5);
    
    // Secondary search: Contact-based matching (only if we have contact info)
    let contactSuggestions = [];
    if (sourceCustomer.importedContactEmail || sourceCustomer.importedContactName) {
      const contactFuse = new Fuse(availableTargets, {
        keys: [
          { name: 'contacts.email', weight: 0.6 },
          { name: 'contacts.firstName', weight: 0.2 },
          { name: 'contacts.lastName', weight: 0.2 }
        ],
        threshold: 0.3, // More lenient for contact matching
        includeScore: true,
        ignoreLocation: true
      });
      
      // Search by contact email first (most reliable)
      if (sourceCustomer.importedContactEmail) {
        const contactResults = contactFuse.search(sourceCustomer.importedContactEmail);
        contactSuggestions = contactResults.slice(0, 3).map(result => ({
          customer: result.item,
          similarity: Math.round((1 - result.score) * 100),
          matchReason: 'Contact Email'
        }));
      }
      // If no email matches, try contact name
      else if (sourceCustomer.importedContactName && contactSuggestions.length === 0) {
        const contactResults = contactFuse.search(sourceCustomer.importedContactName);
        contactSuggestions = contactResults.slice(0, 3).map(result => ({
          customer: result.item,
          similarity: Math.round((1 - result.score) * 100),
          matchReason: 'Contact Name'
        }));
      }
    }
    
    // Combine results: Name matches first, then contact matches (deduplicated)
    const allSuggestions = [...nameSuggestions];
    const existingIds = new Set(nameSuggestions.map(s => s.customer.id));
    
    contactSuggestions.forEach(contactSugg => {
      if (!existingIds.has(contactSugg.customer.id)) {
        allSuggestions.push(contactSugg);
      }
    });
    
    // Return top 5 overall, sorted by similarity
    return allSuggestions
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5);
  }, [sourceCustomer, availableTargets]);
  
  // Filter customers by search term
  const filteredcustomers = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return availableTargets.filter(customer => {
      return (
        (customer.customerName || '').toLowerCase().includes(searchLower) ||
        (customer.customerEmail || '').toLowerCase().includes(searchLower) ||
        (customer.customerPhone || '').toLowerCase().includes(searchLower) ||
        (customer.customerAccountNumber || '').toString().includes(searchLower) ||
        (customer.apparelMagicId || '').toString().includes(searchLower)
      );
    }).slice(0, 20); // Limit to 20 results for performance
  }, [availableTargets, searchTerm]);
  
  // Load merge preview when target is selected
  useEffect(() => {
    if (!selectedTarget) {
      setPreview(null);
      setShowPreview(false);
      return;
    }
    
    setLoadingPreview(true);
    generateMergePreview(sourceCustomer, selectedTarget)
      .then(previewData => {
        setPreview(previewData);
        setLoadingPreview(false);
      })
      .catch(error => {
        console.error('Error loading preview:', error);
        setLoadingPreview(false);
      });
  }, [selectedTarget, sourceCustomer]);
  
  const handleSelectTarget = (customer) => {
    setSelectedTarget(customer);
    setShowPreview(true);
  };
  
  const handleConfirm = () => {
    if (!selectedTarget) return;
    onConfirmMerge(sourceCustomer, selectedTarget);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <h2 className="text-xl font-bold">Match Customer for Merge</h2>
          <p className="text-sm text-blue-100 mt-1">
            Select the Customer to Merge "{sourceCustomer.customerName}" into
          </p>
        </div>
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* source customer Info */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Customer to Merge:</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Name:</span> {sourceCustomer.customerName}</p>
              {sourceCustomer.customerEmail && (
                <p><span className="font-medium">Email:</span> {sourceCustomer.customerEmail}</p>
              )}
              {sourceCustomer.customerPhone && (
                <p><span className="font-medium">Phone:</span> {sourceCustomer.customerPhone}</p>
              )}
              {sourceCustomer.customerAccountNumber && (
                <p><span className="font-medium">Customer ID:</span> {sourceCustomer.customerAccountNumber}</p>
              )}
              {sourceCustomer.apparelMagicId && (
                <p><span className="font-medium">ApparelMagic ID:</span> {sourceCustomer.apparelMagicId}</p>
              )}
              {/* Show imported contact info if available (from Smart Import) */}
              {(sourceCustomer.importedContactName || sourceCustomer.importedContactEmail || sourceCustomer.importedContactPhone) && (
                <>
                  <div className="mt-3 pt-3 border-t border-yellow-300">
                    <p className="font-medium text-gray-700 mb-1">📋 Imported Contact Info:</p>
                  </div>
                  {sourceCustomer.importedContactName && (
                    <p><span className="font-medium">Contact Name:</span> {sourceCustomer.importedContactName}</p>
                  )}
                  {sourceCustomer.importedContactEmail && (
                    <p><span className="font-medium">Contact Email:</span> {sourceCustomer.importedContactEmail}</p>
                  )}
                  {sourceCustomer.importedContactPhone && (
                    <p><span className="font-medium">Contact Phone:</span> {sourceCustomer.importedContactPhone}</p>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Loading State */}
          {loadingCustomers && (
            <div className="mb-6 text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading customers...</p>
            </div>
          )}
          
          {/* Suggested Matches */}
          {!loadingCustomers && suggestedMatches.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Suggested Matches:</h3>
              <div className="space-y-2">
                {suggestedMatches.map(({ customer, similarity, matchReason }) => (
                  <button
                    key={customer.id}
                    onClick={() => handleSelectTarget(customer)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-blue-50 transition-colors ${
                      selectedTarget?.id === customer.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{customer.customerName}</p>
                        <div className="text-sm text-gray-600 space-x-4 mt-1">
                          {customer.customerEmail && <span>📧 {customer.customerEmail}</span>}
                          {customer.customerPhone && <span>📞 {customer.customerPhone}</span>}
                          {customer.customerAccountNumber && (
                            <span className="text-blue-600 font-medium">
                              ID: {customer.customerAccountNumber}
                            </span>
                          )}
                          {customer.apparelMagicId && (
                            <span className="text-green-600 font-medium">
                              AM ID: {customer.apparelMagicId}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex flex-col items-end gap-1">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          similarity >= 80 ? 'bg-green-100 text-green-800' :
                          similarity >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {similarity}% match
                        </span>
                        <span className="text-xs text-gray-500">{matchReason}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Manual Search */}
          {!loadingCustomers && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Or Search Manually:</h3>
              <input
                type="text"
                placeholder="Search by name, email, phone, or ApparelMagic ID..."
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            
            {searchTerm && filteredcustomers.length > 0 && (
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                {filteredcustomers.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => handleSelectTarget(customer)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-blue-50 transition-colors ${
                      selectedTarget?.id === customer.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{customer.customerName}</p>
                    <div className="text-sm text-gray-600 space-x-4 mt-1">
                      {customer.customerEmail && <span>📧 {customer.customerEmail}</span>}
                      {customer.customerPhone && <span>📞 {customer.customerPhone}</span>}
                      {customer.customerAccountNumber && (
                        <span className="text-blue-600 font-medium">
                          ID: {customer.customerAccountNumber}
                        </span>
                      )}
                      {customer.apparelMagicId && (
                        <span className="text-green-600 font-medium">
                          AM ID: {customer.apparelMagicId}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {searchTerm && filteredcustomers.length === 0 && (
              <p className="mt-3 text-sm text-gray-500 text-center">
                No customers found matching "{searchTerm}"
              </p>
            )}
            </div>
          )}
          
          {/* Merge Preview */}
          {showPreview && selectedTarget && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3">Merge Preview:</h3>
              
              {loadingPreview ? (
                <p className="text-sm text-gray-600">Loading preview...</p>
              ) : preview ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="font-medium text-gray-700">Target customer:</span>
                    <span className="text-gray-900">{selectedTarget.customerName}</span>
                    {selectedTarget.customerAccountNumber && (
                      <span className="text-blue-600 font-medium">
                        (ID: {selectedTarget.customerAccountNumber})
                      </span>
                    )}
                    {selectedTarget.apparelMagicId && (
                      <span className="text-green-600 font-medium">
                        (AM ID: {selectedTarget.apparelMagicId})
                      </span>
                    )}
                  </div>
                  
                  <div className="border-t border-blue-200 pt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      The following records will be moved:
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">🎯</span>
                        <div>
                          <p className="font-medium text-gray-900">{preview.counts.opportunities}</p>
                          <p className="text-gray-600">Opportunities</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">📦</span>
                        <div>
                          <p className="font-medium text-gray-900">{preview.counts.orders}</p>
                          <p className="text-gray-600">Orders</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">👤</span>
                        <div>
                          <p className="font-medium text-gray-900">{preview.counts.contacts}</p>
                          <p className="text-gray-600">Contacts</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">✅</span>
                        <div>
                          <p className="font-medium text-gray-900">{preview.counts.tasks}</p>
                          <p className="text-gray-600">Tasks</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-blue-200 pt-3">
                    <p className="text-sm text-gray-600">
                      ℹ️ The source customer will be marked as merged and hidden from normal views.
                      All activity history will be preserved.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedTarget || loadingPreview}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loadingPreview ? 'Loading...' : 'Confirm Merge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerMatchDialog;







